<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ZapiAPI from 'dashboard/api/zapi';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const statusData = ref(null);
const isCheckingStatus = ref(false);
const qrCodeData = ref(null);
const isLoadingQr = ref(false);
const phoneNumber = ref('');
const phoneCodeData = ref(null);
const isRestarting = ref(false);
const profileName = ref('');
const profileDescription = ref('');
const profilePictureUrl = ref('');
const isSavingProfile = ref(false);
const isUpdatingWebhook = ref(false);

const inboxId = computed(() => props.inbox.id);

async function checkStatus() {
  isCheckingStatus.value = true;
  try {
    const { data } = await ZapiAPI.getStatus(inboxId.value);
    statusData.value = data;
  } catch {
    statusData.value = { connected: false };
  } finally {
    isCheckingStatus.value = false;
  }
}

async function loadQrCode() {
  isLoadingQr.value = true;
  try {
    const { data } = await ZapiAPI.getQrCode(inboxId.value);
    qrCodeData.value = data;
  } catch {
    qrCodeData.value = null;
  } finally {
    isLoadingQr.value = false;
  }
}

async function getPhoneCode() {
  if (!phoneNumber.value) return;
  try {
    const { data } = await ZapiAPI.getPhoneCode(
      inboxId.value,
      phoneNumber.value
    );
    phoneCodeData.value = data;
  } catch {
    useAlert(t('INBOX_MGMT.ZAPI_MANAGEMENT.QR_CODE.PHONE_CODE_DESC'));
  }
}

async function restartInstance() {
  isRestarting.value = true;
  try {
    await ZapiAPI.restart(inboxId.value);
    useAlert(t('INBOX_MGMT.ZAPI_MANAGEMENT.RESTART.SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.ZAPI_MANAGEMENT.RESTART.ERROR'));
  } finally {
    isRestarting.value = false;
  }
}

async function updateWebhook() {
  isUpdatingWebhook.value = true;
  try {
    await ZapiAPI.updateWebhook(inboxId.value);
    useAlert(t('INBOX_MGMT.ZAPI_MANAGEMENT.WEBHOOK.SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.ZAPI_MANAGEMENT.WEBHOOK.ERROR'));
  } finally {
    isUpdatingWebhook.value = false;
  }
}

async function saveProfile() {
  isSavingProfile.value = true;
  try {
    const profileData = {};
    if (profileName.value) profileData.name = profileName.value;
    if (profileDescription.value)
      profileData.description = profileDescription.value;
    if (profilePictureUrl.value)
      profileData.picture_url = profilePictureUrl.value;

    await ZapiAPI.updateProfile(inboxId.value, profileData);
    useAlert(t('INBOX_MGMT.ZAPI_MANAGEMENT.PROFILE.SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.ZAPI_MANAGEMENT.PROFILE.ERROR'));
  } finally {
    isSavingProfile.value = false;
  }
}
</script>

<template>
  <div class="mx-6 max-w-4xl">
    <div class="mb-6">
      <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100">
        {{ $t('INBOX_MGMT.ZAPI_MANAGEMENT.TITLE') }}
      </h3>
      <p class="text-sm text-slate-600 dark:text-slate-300">
        {{ $t('INBOX_MGMT.ZAPI_MANAGEMENT.DESCRIPTION') }}
      </p>
    </div>

    <!-- Connection Status -->
    <div class="mb-6 rounded-lg border border-slate-100 dark:border-slate-700 p-4">
      <h4 class="text-base font-medium text-slate-800 dark:text-slate-100 mb-2">
        {{ $t('INBOX_MGMT.ZAPI_MANAGEMENT.STATUS.TITLE') }}
      </h4>
      <div class="flex items-center gap-3">
        <NextButton
          :is-loading="isCheckingStatus"
          :label="
            isCheckingStatus
              ? $t('INBOX_MGMT.ZAPI_MANAGEMENT.STATUS.CHECKING')
              : $t('INBOX_MGMT.ZAPI_MANAGEMENT.STATUS.CHECK_BUTTON')
          "
          blue
          solid
          @click="checkStatus"
        />
        <span
          v-if="statusData"
          class="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium"
          :class="
            statusData.connected
              ? 'bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-300'
              : 'bg-red-50 text-red-700 dark:bg-red-900 dark:text-red-300'
          "
        >
          <span
            class="h-2 w-2 rounded-full"
            :class="statusData.connected ? 'bg-green-500' : 'bg-red-500'"
          />
          {{
            statusData.connected
              ? $t('INBOX_MGMT.ZAPI_MANAGEMENT.STATUS.CONNECTED')
              : $t('INBOX_MGMT.ZAPI_MANAGEMENT.STATUS.DISCONNECTED')
          }}
        </span>
      </div>
    </div>

    <!-- Webhook -->
    <div class="mb-6 rounded-lg border border-slate-100 dark:border-slate-700 p-4">
      <h4 class="text-base font-medium text-slate-800 dark:text-slate-100 mb-2">
        {{ $t('INBOX_MGMT.ZAPI_MANAGEMENT.WEBHOOK.TITLE') }}
      </h4>
      <p class="text-sm text-slate-600 dark:text-slate-300 mb-3">
        {{ $t('INBOX_MGMT.ZAPI_MANAGEMENT.WEBHOOK.DESCRIPTION') }}
      </p>
      <NextButton
        :is-loading="isUpdatingWebhook"
        :label="$t('INBOX_MGMT.ZAPI_MANAGEMENT.WEBHOOK.BUTTON')"
        blue
        solid
        @click="updateWebhook"
      />
    </div>

    <!-- QR Code -->
    <div class="mb-6 rounded-lg border border-slate-100 dark:border-slate-700 p-4">
      <h4 class="text-base font-medium text-slate-800 dark:text-slate-100 mb-2">
        {{ $t('INBOX_MGMT.ZAPI_MANAGEMENT.QR_CODE.TITLE') }}
      </h4>
      <p class="text-sm text-slate-600 dark:text-slate-300 mb-3">
        {{ $t('INBOX_MGMT.ZAPI_MANAGEMENT.QR_CODE.DESCRIPTION') }}
      </p>
      <NextButton
        :is-loading="isLoadingQr"
        :label="
          isLoadingQr
            ? $t('INBOX_MGMT.ZAPI_MANAGEMENT.QR_CODE.LOADING')
            : $t('INBOX_MGMT.ZAPI_MANAGEMENT.QR_CODE.REFRESH_BUTTON')
        "
        blue
        solid
        @click="loadQrCode"
      />
      <div v-if="qrCodeData" class="mt-4">
        <img
          v-if="qrCodeData.value"
          :src="qrCodeData.value"
          alt="QR Code"
          class="h-64 w-64 rounded-lg border border-slate-200 dark:border-slate-600"
        />
        <pre
          v-else
          class="mt-2 rounded bg-slate-50 dark:bg-slate-800 p-3 text-sm"
          >{{ JSON.stringify(qrCodeData, null, 2) }}</pre
        >
      </div>

      <!-- Phone Code Alternative -->
      <div class="mt-4 border-t border-slate-100 dark:border-slate-700 pt-4">
        <h5 class="text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
          {{ $t('INBOX_MGMT.ZAPI_MANAGEMENT.QR_CODE.PHONE_CODE_TITLE') }}
        </h5>
        <p class="text-sm text-slate-600 dark:text-slate-300 mb-2">
          {{ $t('INBOX_MGMT.ZAPI_MANAGEMENT.QR_CODE.PHONE_CODE_DESC') }}
        </p>
        <div class="flex items-end gap-2">
          <label class="mb-0 flex-1">
            <input
              v-model="phoneNumber"
              type="text"
              :placeholder="
                $t('INBOX_MGMT.ZAPI_MANAGEMENT.QR_CODE.PHONE_PLACEHOLDER')
              "
            />
          </label>
          <NextButton
            :label="$t('INBOX_MGMT.ZAPI_MANAGEMENT.QR_CODE.GET_CODE_BUTTON')"
            blue
            solid
            @click="getPhoneCode"
          />
        </div>
        <pre
          v-if="phoneCodeData"
          class="mt-2 rounded bg-slate-50 dark:bg-slate-800 p-3 text-sm"
          >{{ JSON.stringify(phoneCodeData, null, 2) }}</pre
        >
      </div>
    </div>

    <!-- Restart -->
    <div class="mb-6 rounded-lg border border-slate-100 dark:border-slate-700 p-4">
      <h4 class="text-base font-medium text-slate-800 dark:text-slate-100 mb-2">
        {{ $t('INBOX_MGMT.ZAPI_MANAGEMENT.RESTART.TITLE') }}
      </h4>
      <p class="text-sm text-slate-600 dark:text-slate-300 mb-3">
        {{ $t('INBOX_MGMT.ZAPI_MANAGEMENT.RESTART.DESCRIPTION') }}
      </p>
      <NextButton
        :is-loading="isRestarting"
        :label="$t('INBOX_MGMT.ZAPI_MANAGEMENT.RESTART.BUTTON')"
        blue
        solid
        @click="restartInstance"
      />
    </div>

    <!-- Profile -->
    <div class="mb-6 rounded-lg border border-slate-100 dark:border-slate-700 p-4">
      <h4 class="text-base font-medium text-slate-800 dark:text-slate-100 mb-2">
        {{ $t('INBOX_MGMT.ZAPI_MANAGEMENT.PROFILE.TITLE') }}
      </h4>
      <form class="flex flex-col gap-3" @submit.prevent="saveProfile">
        <label>
          {{ $t('INBOX_MGMT.ZAPI_MANAGEMENT.PROFILE.NAME_LABEL') }}
          <input
            v-model="profileName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ZAPI_MANAGEMENT.PROFILE.NAME_PLACEHOLDER')
            "
          />
        </label>
        <label>
          {{ $t('INBOX_MGMT.ZAPI_MANAGEMENT.PROFILE.DESCRIPTION_LABEL') }}
          <input
            v-model="profileDescription"
            type="text"
            :placeholder="
              $t(
                'INBOX_MGMT.ZAPI_MANAGEMENT.PROFILE.DESCRIPTION_PLACEHOLDER'
              )
            "
          />
        </label>
        <label>
          {{ $t('INBOX_MGMT.ZAPI_MANAGEMENT.PROFILE.PICTURE_LABEL') }}
          <input
            v-model="profilePictureUrl"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ZAPI_MANAGEMENT.PROFILE.PICTURE_PLACEHOLDER')
            "
          />
        </label>
        <div>
          <NextButton
            :is-loading="isSavingProfile"
            type="submit"
            :label="$t('INBOX_MGMT.ZAPI_MANAGEMENT.PROFILE.SAVE_BUTTON')"
            blue
            solid
          />
        </div>
      </form>
    </div>
  </div>
</template>
